import React, { useMemo } from 'react'

import { useRemoteConfigPayload } from '@/lib/config/remote-config'
import { LogoKey, LogoVariantMap, logoKeyToLogo } from '@/lib/ui/logo-key-to-logo'
import { cn } from '@/lib/ui/style-helpers'

import LemaLogoFull from '@/assets/lema-full.svg'
import LemaLogoIcon from '@/assets/lema-icon.svg'

const LOGO_FULL_SIZE = { width: 60, height: 20 }
const LOGO_ICON_SIZE = { width: 20, height: 20 }
type WebsiteLogoVariant = keyof LogoVariantMap

// Return the logo key instead of the component to avoid creating components during render
const useGetWebsiteLogo = (orgId?: string): LogoKey | 'lema' => {
  const { payload: orgIdToLogoKeyPayload, isLoading } = useRemoteConfigPayload('org-id-to-logo-key')

  return useMemo(() => {
    if (isLoading || !orgId) {
      return 'lema'
    }

    if (orgIdToLogoKeyPayload?.[orgId]) {
      return orgIdToLogoKeyPayload[orgId] as LogoKey
    }

    return 'lema'
  }, [isLoading, orgId, orgIdToLogoKeyPayload])
}

interface WebsiteLogoProps {
  orgId?: string
  /** 'full' = 60×20px, 'icon' = 20×20px. Default 'full'. */
  variant?: WebsiteLogoVariant
  className?: string
}

export const WebsiteLogo: React.FC<WebsiteLogoProps> = ({ orgId, variant = 'full', className }) => {
  const logoKey = useGetWebsiteLogo(orgId)
  const logo = logoKey === 'lema' ? undefined : logoKeyToLogo[logoKey]

  const isIcon = variant === 'icon'
  const size =
    (isIcon ? logo?.iconSize : logo?.fullSize) ?? (isIcon ? LOGO_ICON_SIZE : LOGO_FULL_SIZE)
  const sharedClassName = cn('shrink-0 text-main-foreground-primary', className)
  const Component = logo?.[variant] ?? (isIcon ? LemaLogoIcon : LemaLogoFull)

  return <Component className={sharedClassName} width={size.width} height={size.height} />
}
